import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card,
  Typography,
  Form,
  Input,
  Select,
  Button,
  Space,
  Row,
  Col,
  Table,
  Tag,
  Tabs,
  message,
  DatePicker,
  InputNumber,
  Divider,
  Alert,
  Upload,
} from 'antd';
import { SendOutlined, TeamOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { parsePlayerIdList } from '@/utils/playerIdList';
import type { Dayjs } from 'dayjs';
import type { NotificationHistory } from '@/types';
import { LifecyclePushesTab } from './LifecyclePushesTab';

const { Title, Text } = Typography;
const { TextArea } = Input;

const COHORT_SEARCH_DEBOUNCE_MS = 300;

interface CohortOption {
  id: string;
  name: string;
  memberCount: number;
}

interface NotificationFormValues {
  title: string;
  body: string;
  targetingType: 'all' | 'segment' | 'userList' | 'cohort';
  cohortId?: string;
  segment?: {
    minDivision?: number;
    maxDivision?: number;
    platform?: 'android' | 'ios';
  };
  schedulingType: 'immediate' | 'scheduled';
  scheduledAt?: Dayjs;
}

export const LiveOpsPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [segmentCount, setSegmentCount] = useState<number | null>(null);
  const [targetingType, setTargetingType] = useState<string>('all');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState<NotificationHistory[]>([]);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [cohorts, setCohorts] = useState<CohortOption[]>([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);
  // The picked cohort stays in the options when a later search leaves it out.
  const [pickedCohort, setPickedCohort] = useState<CohortOption | null>(null);
  const cohortSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  const [playerText, setPlayerText] = useState('');
  const playerList = useMemo(
    () => (playerText.trim() ? parsePlayerIdList(playerText) : null),
    [playerText]
  );
  const playerListBlocked =
    targetingType === 'userList' &&
    (!playerList || playerList.ids.length === 0 || playerList.invalid.length > 0);

  const fetchNotificationHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await adminApi.getNotificationHistory();
      const data = response.data?.data || response.data;
      setNotificationHistory(Array.isArray(data) ? data : []);
      setApiAvailable(true);
    } catch (error: unknown) {
      const err = error as { response?: { status?: number }; code?: string };
      if (err.response?.status === 404 || err.code === 'ERR_NETWORK') {
        setApiAvailable(false);
      } else if (err.response?.status === 500) {
        // Backend error - show feature as unavailable
        setApiAvailable(false);
        console.error('LiveOps API returned 500:', error);
      } else {
        console.error('Failed to fetch notification history:', error);
      }
      setNotificationHistory([]); // Ensure empty array on error
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotificationHistory();
  }, [fetchNotificationHistory]);

  // Searched on the server by name, so every cohort can be found, not only the first page.
  const fetchCohorts = useCallback(async (search?: string) => {
    setCohortsLoading(true);
    try {
      const response = await adminApi.getCohorts(
        search?.trim() ? { search: search.trim(), limit: 50 } : { limit: 50 }
      );
      const data = response.data?.cohorts || response.data?.data || response.data;
      setCohorts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch cohorts:', error);
      message.error('Failed to load cohorts');
    } finally {
      setCohortsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (targetingType === 'cohort' && cohorts.length === 0) fetchCohorts();
  }, [targetingType, cohorts.length, fetchCohorts]);

  useEffect(() => () => clearTimeout(cohortSearchTimer.current), []);

  const onCohortSearch = (text: string) => {
    clearTimeout(cohortSearchTimer.current);
    cohortSearchTimer.current = setTimeout(() => fetchCohorts(text), COHORT_SEARCH_DEBOUNCE_MS);
  };

  const cohortOptions =
    pickedCohort && !cohorts.some((c) => c.id === pickedCohort.id)
      ? [pickedCohort, ...cohorts]
      : cohorts;

  const onPlayerText = (text: string) => {
    setPlayerText(text);
    setSegmentCount(null);
  };

  const readPlayerFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => onPlayerText(String(reader.result ?? ''));
    reader.onerror = () => message.error('Could not read the file');
    reader.readAsText(file);
  };

  const targetingPayload = (values: NotificationFormValues) => {
    if (values.targetingType === 'cohort') return { type: 'cohort', cohortId: values.cohortId };
    if (values.targetingType === 'userList') {
      return { type: 'userList', userIds: playerList?.ids ?? [] };
    }
    return { type: values.targetingType, segment: values.segment };
  };

  const handleSendNotification = async (values: NotificationFormValues) => {
    setLoading(true);
    try {
      const payload = {
        title: values.title,
        body: values.body,
        targeting: targetingPayload(values),
        scheduling: {
          type: values.schedulingType,
          scheduledAt: values.scheduledAt ? values.scheduledAt.toISOString() : undefined,
        },
      };

      const response = await adminApi.sendNotification(payload);
      // The backend answers before the audience is built; the recipient count
      // lands in Notification History.
      message.success(
        response.data.message || `Notification sent to ${response.data.recipientCount} users`
      );
      form.resetFields();
      setSegmentCount(null);
      setPlayerText('');
      setTargetingType('all');
      fetchNotificationHistory();
      setTimeout(fetchNotificationHistory, 15000);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to send notification'));
    } finally {
      setLoading(false);
    }
  };

  const handleGetSegmentCount = async () => {
    const segment =
      targetingType === 'cohort'
        ? { cohortId: form.getFieldValue('cohortId') }
        : targetingType === 'userList'
          ? { userIds: playerList?.ids ?? [] }
          : form.getFieldValue('segment') || {};
    try {
      const response = await adminApi.getSegmentCount(segment);
      setSegmentCount(response.data.count);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to get segment count'));
    }
  };

  const predefinedSegments = [
    { label: 'All Users', value: 'all' },
    { label: 'Whales (>$500 spent)', value: 'whale' },
    { label: 'Dolphins ($50-$500)', value: 'dolphin' },
    { label: 'Non-Payers', value: 'non_payer' },
    { label: 'New Users (<7 days)', value: 'new_users' },
    { label: 'Churned (7+ days inactive)', value: 'churned' },
    { label: 'Champions (Div 15+)', value: 'champions' },
  ];

  const historyColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Recipients',
      dataIndex: 'recipientCount',
      key: 'recipientCount',
      render: (count: number) => count?.toLocaleString() || '-',
    },
    {
      title: 'Sent By',
      dataIndex: 'sentBy',
      key: 'sentBy',
    },
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      key: 'sentAt',
      render: (date: string) => (date ? new Date(date).toLocaleString() : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          sent: 'success',
          scheduled: 'processing',
          failed: 'error',
          cancelled: 'default',
        };
        return <Tag color={colors[status]}>{status?.toUpperCase()}</Tag>;
      },
    },
  ];

  if (!apiAvailable) {
    return (
      <div>
        <div className="page-header">
          <Title level={3}>LiveOps Center</Title>
          <Text type="secondary">Manage push notifications and announcements</Text>
        </div>
        <Alert
          message="Feature Not Available"
          description="The LiveOps API endpoints are not yet implemented on the backend. This feature will be available once the backend support is added."
          type="info"
          showIcon
          style={{ marginTop: 24 }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={3}>LiveOps Center</Title>
        <Text type="secondary">Manage push notifications and announcements</Text>
      </div>

      <Tabs
        items={[
          {
            key: 'send',
            label: 'Send Notification',
            children: (
              <Row gutter={24}>
                <Col xs={24} lg={14}>
                  <Card title="Create Push Notification">
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleSendNotification}
                      initialValues={{
                        targetingType: 'all',
                        schedulingType: 'immediate',
                      }}
                    >
                      <Form.Item
                        name="title"
                        label="Title"
                        rules={[{ required: true, message: 'Please enter a title' }]}
                      >
                        <Input placeholder="Notification title" maxLength={50} showCount />
                      </Form.Item>

                      <Form.Item
                        name="body"
                        label="Body"
                        rules={[{ required: true, message: 'Please enter a message' }]}
                      >
                        <TextArea
                          placeholder="Notification message"
                          rows={3}
                          maxLength={200}
                          showCount
                        />
                      </Form.Item>

                      <Divider>Targeting</Divider>

                      <Form.Item name="targetingType" label="Target Audience">
                        <Select
                          onChange={(val) => {
                            setTargetingType(val);
                            setSegmentCount(null);
                          }}
                        >
                          <Select.Option value="all">All Users</Select.Option>
                          <Select.Option value="segment">Custom Segment</Select.Option>
                          <Select.Option value="userList">User List (CSV)</Select.Option>
                          <Select.Option value="cohort">Cohort</Select.Option>
                        </Select>
                      </Form.Item>

                      {targetingType === 'cohort' && (
                        <>
                          <Form.Item
                            name="cohortId"
                            label="Cohort"
                            rules={[{ required: true, message: 'Please pick a cohort' }]}
                            extra="Only members whose membership has not expired, with push on and the app still installed, are sent to."
                          >
                            <Select
                              showSearch
                              filterOption={false}
                              onSearch={onCohortSearch}
                              loading={cohortsLoading}
                              placeholder="Type to search cohorts by name"
                              notFoundContent={cohortsLoading ? 'Searching...' : 'No cohort found'}
                              onChange={(id: string) => {
                                setSegmentCount(null);
                                setPickedCohort(cohortOptions.find((c) => c.id === id) ?? null);
                              }}
                              options={cohortOptions.map((c) => ({
                                value: c.id,
                                label: `${c.name} (${(c.memberCount ?? 0).toLocaleString()} members)`,
                              }))}
                            />
                          </Form.Item>

                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => (
                              <Button
                                icon={<TeamOutlined />}
                                onClick={handleGetSegmentCount}
                                disabled={!getFieldValue('cohortId')}
                              >
                                Get Reachable Count
                              </Button>
                            )}
                          </Form.Item>
                          {segmentCount !== null && (
                            <Text style={{ marginLeft: 16 }}>
                              <strong>{segmentCount.toLocaleString()}</strong> members can be pushed
                            </Text>
                          )}
                        </>
                      )}

                      {targetingType === 'userList' && (
                        <>
                          <Form.Item
                            label="Player ids"
                            extra="Paste player ids, or upload a .csv or .txt with one id per line (a userId header is fine). Up to 10,000. Only players with push on and the app still installed are sent to."
                          >
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <Upload
                                accept=".csv,.txt"
                                showUploadList={false}
                                beforeUpload={(file) => {
                                  readPlayerFile(file);
                                  return false;
                                }}
                              >
                                <Button icon={<UploadOutlined />}>Upload CSV</Button>
                              </Upload>
                              <TextArea
                                rows={6}
                                value={playerText}
                                onChange={(e) => onPlayerText(e.target.value)}
                                placeholder="One player id per line"
                              />
                            </Space>
                          </Form.Item>
                          {playerList && (
                            <Space direction="vertical" style={{ marginBottom: 16 }}>
                              <Text>
                                <strong>{playerList.ids.length.toLocaleString()}</strong> valid,{' '}
                                <Text type={playerList.invalid.length ? 'danger' : undefined}>
                                  <strong>{playerList.invalid.length.toLocaleString()}</strong>{' '}
                                  invalid
                                </Text>
                              </Text>
                              {playerList.invalid.length > 0 && (
                                <Text type="danger">
                                  Fix or remove before sending:{' '}
                                  {playerList.invalid.slice(0, 10).join(', ')}
                                  {playerList.invalid.length > 10 ? ', ...' : ''}
                                </Text>
                              )}
                            </Space>
                          )}
                          <div>
                            <Button
                              icon={<TeamOutlined />}
                              onClick={handleGetSegmentCount}
                              disabled={playerListBlocked}
                            >
                              Get Reachable Count
                            </Button>
                            {segmentCount !== null && (
                              <Text style={{ marginLeft: 16 }}>
                                <strong>{segmentCount.toLocaleString()}</strong> players can be
                                pushed
                              </Text>
                            )}
                          </div>
                        </>
                      )}

                      {targetingType === 'segment' && (
                        <>
                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item name={['segment', 'minDivision']} label="Min Division">
                                <InputNumber min={1} max={21} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item name={['segment', 'maxDivision']} label="Max Division">
                                <InputNumber min={1} max={21} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Form.Item name={['segment', 'platform']} label="Platform">
                            <Select allowClear>
                              <Select.Option value="android">Android</Select.Option>
                              <Select.Option value="ios">iOS</Select.Option>
                            </Select>
                          </Form.Item>

                          <Button icon={<TeamOutlined />} onClick={handleGetSegmentCount}>
                            Get Segment Count
                          </Button>
                          {segmentCount !== null && (
                            <Text style={{ marginLeft: 16 }}>
                              <strong>{segmentCount.toLocaleString()}</strong> users match this
                              segment
                            </Text>
                          )}
                        </>
                      )}

                      <Divider>Scheduling</Divider>

                      <Form.Item name="schedulingType" label="When to Send">
                        <Select>
                          <Select.Option value="immediate">Send Immediately</Select.Option>
                          <Select.Option value="scheduled">Schedule for Later</Select.Option>
                        </Select>
                      </Form.Item>

                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) =>
                          getFieldValue('schedulingType') === 'scheduled' && (
                            <Form.Item
                              name="scheduledAt"
                              label="Scheduled Time"
                              rules={[{ required: true, message: 'Pick when to send it' }]}
                              extra="Sent within a minute of this time. More than 15 minutes late (e.g. the scheduler was off) and it is not sent."
                            >
                              <DatePicker showTime style={{ width: '100%' }} />
                            </Form.Item>
                          )
                        }
                      </Form.Item>

                      <Form.Item style={{ marginTop: 24 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          disabled={playerListBlocked}
                          icon={<SendOutlined />}
                          size="large"
                        >
                          Send Notification
                        </Button>
                      </Form.Item>
                    </Form>
                  </Card>
                </Col>

                <Col xs={24} lg={10}>
                  <Card title="Preview">
                    <div className="notification-preview">
                      <div className="notification-preview-title">
                        {form.getFieldValue('title') || 'Notification Title'}
                      </div>
                      <div className="notification-preview-body">
                        {form.getFieldValue('body') || 'Notification message will appear here...'}
                      </div>
                    </div>
                  </Card>

                  <Card title="Quick Segments" style={{ marginTop: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {predefinedSegments.map((seg) => (
                        <Button
                          key={seg.value}
                          block
                          onClick={() => {
                            form.setFieldsValue({
                              targetingType: seg.value === 'all' ? 'all' : 'segment',
                            });
                            setTargetingType(seg.value === 'all' ? 'all' : 'segment');
                          }}
                        >
                          {seg.label}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: 'history',
            label: 'Notification History',
            children: (
              <Card
                extra={
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchNotificationHistory}
                    loading={historyLoading}
                  >
                    Refresh
                  </Button>
                }
              >
                <Table
                  dataSource={notificationHistory}
                  columns={historyColumns}
                  rowKey="id"
                  loading={historyLoading}
                  pagination={{ pageSize: 20 }}
                  locale={{ emptyText: 'No notifications sent yet' }}
                />
              </Card>
            ),
          },
          {
            key: 'lifecycle',
            label: 'Lifecycle Pushes',
            children: <LifecyclePushesTab />,
          },
          {
            key: 'announcements',
            label: 'Announcements',
            children: (
              <Card>
                <Text type="secondary">
                  In-game announcements will be managed here. Coming soon...
                </Text>
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
};

export default LiveOpsPage;
